import { supabase } from './supabase';
import type { 
  WorkoutSet, 
  CompletedWorkout, 
  CompletedExercise
} from '../types';

/**
 * Service for managing workout session operations
 * Handles workout creation, completion, and session management
 * Implements requirements 5.1, 5.5, 5.6
 */
export class WorkoutService {
  /**
   * Check if running in development mode
   */
  private isDevelopmentMode(): boolean {
    return import.meta.env.VITE_DEV_MODE === 'true';
  }

  /**
   * Complete a workout session with all exercise data
   * Requirements: 5.5, 5.6
   */
  async completeWorkout(workoutData: {
    workout_id: string;
    exercises: Array<{
      exercise_id: string;
      sets: WorkoutSet[];
      notes?: string;
    }>;
    notes?: string;
    duration_minutes: number;
  }, userId?: string, programId?: string): Promise<CompletedWorkout> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: simulating workout completion', workoutData);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const mockCompletedWorkout: CompletedWorkout = {
        id: workoutData.workout_id,
        user_id: 'mock-user-123456789',
        program_workout_id: 'mock-program-workout-id',
        workout_name: 'Mock Workout',
        completed_at: new Date().toISOString(),
        duration_minutes: workoutData.duration_minutes,
        notes: workoutData.notes,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      return mockCompletedWorkout;
    }

    // Real implementation for database
    try {
      console.log('Starting workout completion process:', { workoutData, userId, programId });

      // Validate input data
      if (!workoutData.workout_id) {
        throw new Error('ID тренировки не указан');
      }

      if (!workoutData.exercises || workoutData.exercises.length === 0) {
        throw new Error('Нет данных об упражнениях');
      }

      // Ensure minimum duration of 1 minute (for very quick workouts)
      const durationMinutes = Math.max(1, workoutData.duration_minutes || 0);

      // Get current user if not provided
      const currentUserId = userId || (await supabase.auth.getUser()).data.user?.id;
      if (!currentUserId) {
        throw new Error('Пользователь не авторизован');
      }

      console.log('User authenticated:', currentUserId);

      // Validate that all exercises have valid sets
      const validExercises = workoutData.exercises.filter(exercise => {
        const validSets = exercise.sets.filter(set => {
          // Different validation based on set data
          if (set.weight !== undefined && set.reps !== undefined) {
            // Weight + reps exercise (allow 0 weight for bodyweight)
            return set.weight >= 0 && set.reps > 0;
          }
          if (set.reps !== undefined && set.weight === undefined) {
            // Bodyweight exercise
            return set.reps > 0;
          }
          if (set.duration_seconds !== undefined) {
            // Time-based exercise
            return set.duration_seconds > 0;
          }
          if (set.distance_meters !== undefined && set.duration_seconds !== undefined) {
            // Distance + time exercise
            return set.distance_meters > 0 && set.duration_seconds > 0;
          }
          return false;
        });
        return validSets.length > 0;
      });

      if (validExercises.length === 0) {
        throw new Error('Нет корректных данных о подходах');
      }

      console.log(`Processing ${validExercises.length} exercises with valid sets`);

      // Create workout record
      const workoutRecord = {
        user_id: currentUserId,
        program_workout_id: workoutData.workout_id,
        started_at: new Date(Date.now() - durationMinutes * 60 * 1000).toISOString(),
        completed_at: new Date().toISOString(),
        comment: workoutData.notes || null
      };

      // Add program_id if provided
      if (programId) {
        (workoutRecord as any).program_id = programId;
      }

      console.log('Creating workout record:', workoutRecord);

      const { data: workout, error: workoutError } = await (supabase
        .from('workouts') as any)
        .insert(workoutRecord)
        .select()
        .single();

      if (workoutError) {
        console.error('Error creating workout:', workoutError);
        throw new Error(`Ошибка при создании тренировки: ${workoutError.message}`);
      }

      console.log('Workout created successfully:', workout);

      // Save all valid sets
      const allSets = [];
      for (const exercise of validExercises) {
        let setNumber = 1;
        for (const set of exercise.sets) {
          // Validate set data based on type
          let isValidSet = false;
          
          if (set.weight !== undefined && set.reps !== undefined) {
            // Weight + reps (allow 0 weight for bodyweight)
            isValidSet = set.weight >= 0 && set.reps > 0;
          } else if (set.reps !== undefined && set.weight === undefined) {
            // Bodyweight exercise
            isValidSet = set.reps > 0;
          } else if (set.duration_seconds !== undefined) {
            // Time-based exercise
            isValidSet = set.duration_seconds > 0;
          } else if (set.distance_meters !== undefined && set.duration_seconds !== undefined) {
            // Distance + time exercise
            isValidSet = set.distance_meters > 0 && set.duration_seconds > 0;
          }
          
          if (!isValidSet) {
            console.warn(`Skipping invalid set:`, set);
            continue;
          }
          
          const setRecord: any = {
            workout_id: workout.id,
            exercise_id: exercise.exercise_id,
            set_number: setNumber,
            comment: set.notes || null,
            completed_at: set.completed_at || new Date().toISOString()
          };

          // Add fields based on what's available
          if (set.weight !== undefined) setRecord.weight = set.weight;
          if (set.reps !== undefined) setRecord.reps = set.reps;
          if (set.duration_seconds !== undefined) setRecord.duration_seconds = set.duration_seconds;
          if (set.distance_meters !== undefined) setRecord.distance_meters = set.distance_meters;
          if (set.bodyweight_modifier) setRecord.bodyweight_modifier = set.bodyweight_modifier;
          if (set.assistance) setRecord.assistance = set.assistance;
          
          allSets.push(setRecord);
          setNumber++;
        }
      }

      if (allSets.length === 0) {
        throw new Error('Нет корректных подходов для сохранения');
      }

      console.log(`Saving ${allSets.length} sets:`, allSets);
      
      const { error: setsError } = await (supabase
        .from('sets') as any)
        .insert(allSets);

      if (setsError) {
        console.error('Error saving sets:', setsError);
        throw new Error(`Ошибка при сохранении подходов: ${setsError.message}`);
      }
      
      console.log('Sets saved successfully');

      const completedWorkout: CompletedWorkout = {
        id: workout.id,
        user_id: workout.user_id,
        program_workout_id: workoutData.workout_id,
        workout_name: 'Completed Workout',
        completed_at: workout.completed_at,
        duration_minutes: durationMinutes,
        notes: workoutData.notes,
        created_at: workout.created_at,
        updated_at: workout.updated_at || workout.created_at
      };

      console.log('Workout completed successfully:', completedWorkout);
      return completedWorkout;

    } catch (error) {
      console.error('Error completing workout:', error);
      if (error instanceof Error) {
        throw new Error(`Ошибка при сохранении тренировки: ${error.message}`);
      }
      throw new Error('Ошибка при сохранении тренировки');
    }
  }

  /**
   * Get all completed workouts for the current user
   * Requirements: 5.6
   */
  async getUserWorkouts(limit: number = 50, offset: number = 0): Promise<CompletedWorkout[]> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock workouts');
      
      const mockWorkouts: CompletedWorkout[] = [
        {
          id: 'mock-workout-1',
          user_id: 'mock-user-123456789',
          program_workout_id: 'mock-program-workout-1',
          workout_name: 'Тренировка A - Верх тела',
          completed_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Yesterday
          duration_minutes: 45,
          notes: 'Отличная тренировка!',
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          updated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'mock-workout-2',
          user_id: 'mock-user-123456789',
          program_workout_id: 'mock-program-workout-2',
          workout_name: 'Тренировка B - Низ тела',
          completed_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
          duration_minutes: 52,
          created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
        }
      ];

      return mockWorkouts.slice(offset, offset + limit);
    }

    // TODO: Real implementation will be added when database is properly configured
    throw new Error('Real workout history not implemented yet. Please use development mode.');
  }

  /**
   * Get detailed workout data including exercises and sets
   * Requirements: 5.6
   */
  async getWorkoutDetails(workoutId: string): Promise<{
    workout: CompletedWorkout;
    exercises: Array<{
      exercise: CompletedExercise;
      sets: WorkoutSet[];
    }>;
  } | null> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock workout details');
      
      const mockWorkout: CompletedWorkout = {
        id: workoutId,
        user_id: 'mock-user-123456789',
        program_workout_id: 'mock-program-workout-1',
        workout_name: 'Тренировка A - Верх тела',
        completed_at: new Date().toISOString(),
        duration_minutes: 45,
        notes: 'Отличная тренировка!',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const mockExercises = [
        {
          exercise: {
            id: 'mock-ce-1',
            completed_workout_id: workoutId,
            exercise_id: 'mock-1',
            order_index: 0,
            notes: 'Хорошая техника'
          },
          sets: [
            {
              id: 'mock-set-1',
              exercise_id: 'mock-1',
              set_number: 1,
              weight: 60,
              reps: 10,
              completed_at: new Date().toISOString()
            },
            {
              id: 'mock-set-2',
              exercise_id: 'mock-1',
              set_number: 2,
              weight: 60,
              reps: 8,
              completed_at: new Date().toISOString()
            }
          ]
        }
      ];

      return {
        workout: mockWorkout,
        exercises: mockExercises
      };
    }

    // TODO: Real implementation will be added when database is properly configured
    throw new Error('Real workout details not implemented yet. Please use development mode.');
  }

  /**
   * Get workout statistics for analytics
   * Requirements: 5.6
   */
  async getWorkoutStats(): Promise<{
    total_workouts: number;
    total_duration_minutes: number;
    average_duration_minutes: number;
    workouts_this_week: number;
    workouts_this_month: number;
  }> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: returning mock workout stats');
      
      return {
        total_workouts: 15,
        total_duration_minutes: 675, // 15 * 45 minutes average
        average_duration_minutes: 45,
        workouts_this_week: 3,
        workouts_this_month: 12
      };
    }

    // TODO: Real implementation will be added when database is properly configured
    throw new Error('Real workout stats not implemented yet. Please use development mode.');
  }

  /**
   * Delete a workout and all associated data
   * Requirements: 5.6
   */
  async deleteWorkout(workoutId: string): Promise<void> {
    if (this.isDevelopmentMode()) {
      console.log('Development mode: simulating workout deletion', workoutId);
      return;
    }

    // TODO: Real implementation will be added when database is properly configured
    throw new Error('Real workout deletion not implemented yet. Please use development mode.');
  }
}

// Export singleton instance
export const workoutService = new WorkoutService();